import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import LegalLinks from '../../components/LegalLinks';
import { toast } from 'sonner';
import { Loader2, Mail, Lock, ArrowLeft } from 'lucide-react';
import employeeApi from '../../lib/employeeApi';
import { setEmployeeToken, setEmployeeUser } from '../../lib/employeeAuth';
import { validateEmail, getErrorMessage } from '../../lib/auth';
import { getNextPathAfterLogin, clearSessionExpiryFlag } from '../../lib/sessionRedirect';
import { storeSessionTokens } from '../../lib/refreshToken';

export default function EmployeeLoginPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateEmail(formData.email)) { toast.error('Please enter a valid email address'); return; }
    setLoading(true);
    try {
      const response = await employeeApi.post('/api/employee/auth/login', formData);
      setEmployeeToken(response.data.token);
      setEmployeeUser(response.data.employee);
      // Also stash the refresh token so silent refresh works on subsequent 401s.
      storeSessionTokens('employee', response.data);
      clearSessionExpiryFlag();
      toast.success('Welcome back!');
      if (response.data.profile_completed === false) {
        navigate('/employee/complete-profile');
      } else {
        // Honour the ?next= param set by the session-expired redirect so
        // users land back on the exact page they were bounced from.
        navigate(getNextPathAfterLogin('/employee/dashboard'), { replace: true });
      }
    } catch (error) { toast.error(getErrorMessage(error)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#111111]" data-testid="employee-login-page">
      <div className={`w-full ${isMobile ? 'px-5' : 'max-w-[420px]'}`}>
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src="/hed-icon.png" alt="HED" className="w-8 h-8 rounded-lg" />
            <div className="w-px h-6 bg-white/20" />
            <img src="/girgit-logo.png" alt="Girgit" className="w-8 h-8 rounded-lg" />
            <span className="text-xl font-bold text-white">Girgit</span>
          </div>
          <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-[#F59E0B] text-white">Employee Portal</span>
        </div>

        <div className="bg-white/[0.08] backdrop-blur-xl border border-white/10 rounded-2xl p-8" data-testid="employee-login-card">
          <h2 className="text-xl font-bold text-white mb-1">Welcome Back</h2>
          <p className="text-sm text-neutral-400 mb-8">Sign in to your workspace</p>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold text-neutral-400 mb-2">Work Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                <input type="email" required value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 bg-white/[0.06] border border-white/10 rounded-xl text-sm text-white placeholder-neutral-500 focus:outline-none focus:border-[#F59E0B]/50 focus:ring-1 focus:ring-[#F59E0B]/30 transition-colors"
                  placeholder="you@halfeatendonut.in" data-testid="employee-email-input" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-neutral-400 mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                <input type="password" required value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 bg-white/[0.06] border border-white/10 rounded-xl text-sm text-white placeholder-neutral-500 focus:outline-none focus:border-[#F59E0B]/50 focus:ring-1 focus:ring-[#F59E0B]/30 transition-colors"
                  placeholder="Enter your password" data-testid="employee-password-input" />
              </div>
            </div>
            <button type="submit" disabled={loading}
              className="w-full py-3 bg-[#F59E0B] hover:bg-[#D97706] text-white font-semibold text-sm rounded-xl disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
              data-testid="employee-login-submit">
              {loading ? <><Loader2 className="animate-spin w-4 h-4" />Signing in...</> : 'Sign In'}
            </button>

            <div className="relative my-2">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10"></div></div>
              <div className="relative flex justify-center">
                <span className="bg-[#111111] px-3 text-[10px] text-neutral-500 uppercase tracking-wider">or</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR
                // REDIRECT URLS, THIS BREAKS THE AUTH.
                const redirectUrl = window.location.origin + '/employee/dashboard';
                window.location.href =
                  'https://auth.emergentagent.com/?redirect=' + encodeURIComponent(redirectUrl);
              }}
              className="w-full py-3 rounded-xl bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 text-white font-medium text-sm flex items-center justify-center gap-2.5 transition-colors"
              data-testid="employee-google-signin"
            >
              <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              Continue with Google
            </button>
          </form>
        </div>

        <div className="mt-8 text-center space-y-3">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-white transition-colors" data-testid="employee-back-home">
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </Link>
          <LegalLinks tone="dark" />
          <p className="text-xs text-neutral-600">&copy; {new Date().getFullYear()} Half Eaten Donut</p>
        </div>
      </div>
    </div>
  );
}
